import {
  Component,
  Input,
  OnInit,
  EventEmitter,
  Output,
  OnDestroy,
  HostListener
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { signUp, signIn, signInWithRedirect } from 'aws-amplify/auth';
import { ReCaptchaV3Service, RecaptchaV3Module } from 'ng-recaptcha';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { first } from 'rxjs';

import { AuthService } from '../../../auth/auth.service';
import { DiscoverService } from '../../services/discover.service';
import { AnalyticsService } from 'app/shared/services/analytics.service';
import { NavigationIntentService } from 'app/shared/services/navigation-intent.service';
import { ProfileStateService } from 'app/shared/services/profile-state.service';
import { MediaService } from 'app/shared/services/media.service';
import { StatsigService } from 'app/shared/services/statsig.service';
import { TokensService } from 'app/shared/services/tokens.service';
import { ProfileService } from 'app/shared/services/profile.service';
import { ReferralTrackingService } from 'app/shared/services/referral-tracking.service';

import { ButtonComponent } from '../button/button.component';
import { InputComponent } from '../input/input.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { RecaptchaTermsComponent } from '../recaptcha-terms/recaptcha-terms.component';
import { PasswordResetComponent } from '../password-reset/password-reset.component';

import { IButtonConfig } from '../../interfaces/button.interfaces';
import { IInputCustomConfig } from '../../interfaces/input.interfaces';
import { ISvgConfig } from '../../interfaces/svg.interfaces';
import { ICreatorDiscoverData } from '../../../interfaces/creator.interfaces';

import { EButtonActionTypes, EButtonTypes } from '../../enums/button.enums';
import { EInputCustomType, EInputSupportMessage, EInputUsage } from '../../enums/input.enums';
import { ELocalStorageKey } from '../../enums/shared.enums';
import { ESvgTypes } from '../../enums/svg.enums';
import { EAuthType, EUserAuthType } from '../../../auth/auth.enums';
import { ERecaptchaTermsLinks, ETermsOfService } from 'app/enums/terms-of-service.enums';
import { EFeatureGates } from 'app/shared/enums/feature-gates.enums';

import { PASSWORD_VALIDATOR_PATTERN } from '../../const/app.constants';

@Component({
  selector: 'stxt-auth-form',
  standalone: true,
  imports: [
    ButtonComponent,
    InputComponent,
    RecaptchaTermsComponent,
    ReactiveFormsModule,
    RecaptchaV3Module,
    SvgIconComponent,
    CommonModule
  ],
  templateUrl: './auth-form.component.html',
  styleUrl: './auth-form.component.scss',
  providers: [ReCaptchaV3Service]
})
export class AuthFormComponent implements OnInit, OnDestroy {
  @Input() creatorId?: string;
  @Output() closeAuth: EventEmitter<boolean> = new EventEmitter<boolean>();
  public authForm: FormGroup;
  public userEmail: string;
  public isSignUp: boolean = true;
  public closeAndNavigate: boolean = false;
  public googleButton: IButtonConfig = {
    fill: EButtonTypes.Empty,
    text: 'Continue with Google',
    middleIcon: 'google'
  };
  public loginButton: IButtonConfig = {
    fill: EButtonTypes.SecondaryFilled,
    text: 'Log in'
  };
  public signUpButton: IButtonConfig = {
    fill: EButtonTypes.SecondaryFilled,
    text: 'Continue with email'
  };
  public emailInput: IInputCustomConfig = {
    placeholder: 'Email',
    supportingText: {
      text: EInputSupportMessage.Email
    },
    type: EInputCustomType.Email,
    usageCase: EInputUsage.Page
  };
  public get passwordInput(): IInputCustomConfig {
    const baseConfig: IInputCustomConfig = {
      placeholder: 'Password',
      supportingText: {
        text: EInputSupportMessage.Password
      },
      type: EInputCustomType.Password,
      usageCase: EInputUsage.Page
    };

    if (this.isSignUp) return baseConfig;
    baseConfig.hintText = {
      text: EInputSupportMessage.ForgotPassword,
      buttonAction: () => {
        this.openForgotPassword();
      },
      isLink: true
    };

    return baseConfig;
  }
  public closeSvg: ISvgConfig = { fill: ESvgTypes.OutlinedDark, name: 'close' };
  public googleSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'google_colorful' };
  public creator: ICreatorDiscoverData;
  public captchaToken: string;
  public ELocalStorageKey = ELocalStorageKey;
  isMobile: boolean;
  userNotFoundError: boolean = false;
  userExistsError: boolean = false;
  passwordMatchError: boolean = false;
  isRecaptchaEnabled: boolean;
  isForgotPasswordEnabled: boolean;

  constructor(
    public formBuilder: FormBuilder,
    public authService: AuthService,
    public activeModal: NgbActiveModal,
    public discoverService: DiscoverService,
    public analyticsService: AnalyticsService,
    public recaptchaV3Service: ReCaptchaV3Service,
    public navigationIntentService: NavigationIntentService,
    public router: Router,
    public profileStateService: ProfileStateService,
    private mediaService: MediaService,
    private readonly statsigService: StatsigService,
    private readonly tokensService: TokensService,
    private readonly modal: NgbModal,
    private readonly profileService: ProfileService,
    private readonly referralTrackingService: ReferralTrackingService
  ) {}

  ngOnInit(): void {
    this.initAuthForm();
    this.checkScreenSize();

    if (this.creatorId) {
      this.discoverService
        .getDiscoverCreatorData(this.creatorId)
        .pipe(first())
        .subscribe(res => {
          this.creator = res.creators[0];
        });
    }
    this.checkRecaptchaFeatureGate();
    this.checkForgotPasswordFeatureGate();
    document.body.style.overflow = 'hidden';
  }

  initAuthForm(): void {
    this.authForm = this.formBuilder.group({
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          this.userNotFoundValidator,
          this.userExistsValidator
        ]
      ],
      password: [
        '',
        [
          Validators.required,
          this.passwordMatchValidator,
          Validators.pattern(PASSWORD_VALIDATOR_PATTERN)
        ]
      ]
    });
  }

  userNotFoundValidator: ValidatorFn = () => {
    return !this.isSignUp && this.userNotFoundError ? { userNotFound: true } : null;
  };

  userExistsValidator: ValidatorFn = () => {
    return this.isSignUp && this.userExistsError ? { userExists: true } : null;
  };

  passwordMatchValidator: ValidatorFn = () => {
    return !this.isSignUp && this.passwordMatchError ? { passwordMatch: true } : null;
  };

  private async checkFeatureGate(flag: EFeatureGates): Promise<boolean> {
    try {
      await this.statsigService.initializeStatsig(null);
      return await this.statsigService.checkFeatureGate(flag);
    } catch {
      return false;
    }
  }

  async checkRecaptchaFeatureGate(): Promise<void> {
    this.isRecaptchaEnabled = await this.checkFeatureGate(EFeatureGates.RECAPTCHA);
  }

  async checkForgotPasswordFeatureGate(): Promise<void> {
    this.isForgotPasswordEnabled = await this.checkFeatureGate(EFeatureGates.FORGOT_PASSWORD);
  }

  executeRecaptcha(): void {
    if (this.isRecaptchaEnabled) {
      this.recaptchaV3Service
        .execute('authFormAction')
        .pipe(first())
        .subscribe({
          next: token => {
            this.captchaToken = token;
            this.submitEmail();
          }
        });
    } else {
      this.submitEmail();
    }
  }

  openTOS(): void {
    window.open(ETermsOfService.TermsOfUse, '_blank');
  }

  openPrivacyPolicy(): void {
    window.open(ETermsOfService.Privacy, '_blank');
  }

  openGTOS(): void {
    window.open(ERecaptchaTermsLinks.TermsOfService, '_blank');
  }

  openGPrivacyPolicy(): void {
    window.open(ERecaptchaTermsLinks.PrivacyPolicy, '_blank');
  }

  submitEmail(): void {
    const userEmail = this.authForm.value.email;
    const password = this.authForm.value.password;

    if (!this.authForm.valid) return;
    this.userEmail = userEmail;
    this.handleCheckAuthType(userEmail, password);
    this.trackAnalytics();
  }

  handleCheckAuthType(userEmail: string, password: string): void {
    if (this.isSignUp) {
      this.handleSignUp(userEmail, password).then(() => {
        this.closeAuth.emit(true);
      });
    } else {
      this.handleSignIn(userEmail, password).then(() => {
        this.closeAuth.emit(true);
      });
    }
  }

  trackAnalytics(): void {
    this.analyticsService.click(
      this.googleButton.text,
      '',
      'Home',
      EButtonActionTypes.TriggerEmailPasswordSignIn,
      ''
    );
  }

  async handleGoogleSignIn() {
    this.googleButton.isLoading = true;
    // Store the intended redirect URL before initiating sign-in
    if (this.navigationIntentService.getIntent()) {
      localStorage.setItem(ELocalStorageKey.RedirectUrl, this.navigationIntentService.getIntent());
    }

    try {
      await signInWithRedirect({
        provider: 'Google'
      });
      // Note: The code after signInWithRedirect won't execute immediately
      // as the user will be redirected to Google's login page
      this.analyticsService.click(
        this.googleButton.text,
        '',
        'Home',
        EButtonActionTypes.TriggerGoogleSignIn,
        ''
      );
    } catch (error) {
      console.error('Google sign-in error:', error);
    } finally {
      this.googleButton.isLoading = false;
    }
  }

  async handleSignUp(userEmail: string, password: string): Promise<void> {
    this.signUpButton.isLoading = true;
    try {
      const referralData = this.analyticsService.getTrackingParams();
      const userAttributes: Record<string, string> = {};

      if (referralData) {
        const keys = [
          'referrer',
          'utm_campaign',
          'utm_content',
          'utm_medium',
          'utm_source',
          'utm_term'
        ];

        for (const key of keys) {
          userAttributes[`custom:${key}`] = referralData[key as keyof typeof referralData] || '';
        }
      }

      await signUp({
        username: userEmail,
        password: password,
        options: {
          userAttributes: userAttributes
        }
      });

      await signIn({ username: userEmail, password: password }).then(() => {
        this.setUser(EUserAuthType.Email_Password, userEmail);
      });

      await this.authService.currentSession(EAuthType.SignUp);
      this.fetchUserTokensBalance();
      this.profileStateService.prefetchProfileData().pipe(first()).subscribe();
      this.mediaService.initialLoad().pipe(first()).subscribe();
      this.signUpButton.isLoading = false;
      this.closeAuth.emit(true);
      this.activeModal.close();
      this.authService.identifyUser(EAuthType.SignUp, userEmail);
      localStorage.removeItem(ELocalStorageKey.ProfileOpened);
      this.referralTrackingService.saveTrackingParamsToProfile();
    } catch (error) {
      if (error instanceof Error && error?.message.includes('User already exists')) {
        this.setEmailSupportingText(EInputSupportMessage.EmailExists, 'log in.');
        this.handleEmailValidation('exists');
      }
      this.signUpButton.isLoading = false;
    }
  }

  async handleSignIn(userEmail: string, password: string) {
    this.loginButton.isLoading = true;
    try {
      await signIn({
        username: userEmail,
        password: password
      }).then(() => this.setUser(EUserAuthType.Email_Password, userEmail));
      await this.authService.currentSession(EAuthType.Login);
      this.fetchUserTokensBalance();
      this.profileStateService.prefetchProfileData().pipe(first()).subscribe();
      this.mediaService.initialLoad().pipe(first()).subscribe();
      this.loginButton.isLoading = false;
      this.closeAuth.emit(true);
      this.activeModal.close();
      this.referralTrackingService.saveTrackingParamsToProfile();
    } catch (error) {
      if (error instanceof Error && error.message.includes('User does not exist')) {
        this.setEmailSupportingText(EInputSupportMessage.EmailNotFound, 'sign up.');
        this.handleEmailValidation('notFound');
      } else if (error instanceof Error && error.message.toLowerCase().includes('password')) {
        this.setPasswordSupportingText(EInputSupportMessage.PasswordMatch, 'Or reset password.');
        this.handlePasswordValidation();
      } else {
        this.setPasswordSupportingText(EInputSupportMessage.PasswordMatch, 'Or reset password.');
        this.handlePasswordValidation();
      }
      this.loginButton.isLoading = false;
    }
  }

  private fetchUserTokensBalance(): void {
    this.tokensService
      .getTokensBalance()
      .pipe(first())
      .subscribe(res => {
        if (res?.data?.balance !== undefined) {
          this.tokensService.tokensBalanceSubject.next(res.data.balance);
        } else {
          this.tokensService.tokensBalanceSubject.next(0);
        }
      });
  }

  private setEmailSupportingText(text: EInputSupportMessage, buttonText?: string): void {
    const emailControl = this.authForm.get('email');
    this.emailInput.supportingText = { text, buttonText };
    this.emailInput.supportingText.buttonAction = () => {
      emailControl.removeValidators(this.userExistsValidator);
      emailControl.updateValueAndValidity();
      this.isSignUp = buttonText === 'sign up.';
    };
  }

  private setForgotPasswordSupportingText(text: EInputSupportMessage): void {
    this.passwordInput.hintText = {
      text,
      buttonAction: () => {
        this.openForgotPassword();
      }
    };
  }

  private setPasswordSupportingText(text: EInputSupportMessage, buttonText?: string): void {
    this.passwordInput.supportingText = { text, buttonText };
    if (buttonText) {
      this.passwordInput.supportingText.buttonAction = () => {
        this.openForgotPassword();
      };
    }
  }

  private handleEmailValidation(validationType: 'exists' | 'notFound'): void {
    const emailControl = this.authForm.get('email');
    if (!emailControl) return;

    const isUserExists = validationType === 'exists';
    const errorFlag = isUserExists ? 'userExistsError' : 'userNotFoundError';
    const validator = isUserExists ? this.userExistsValidator : this.userNotFoundValidator;

    this.applyValidator(emailControl, errorFlag, validator, () => {
      this.isSignUp = !isUserExists;
      this.setEmailSupportingText(EInputSupportMessage.Email, isUserExists ? null : undefined);
    });
  }

  private handlePasswordValidation(): void {
    const passwordControl = this.authForm.get('password');
    if (!passwordControl) return;

    this.applyValidator(passwordControl, 'passwordMatchError', this.passwordMatchValidator, () => {
      this.setPasswordSupportingText(EInputSupportMessage.Password);
    });
  }

  private applyValidator(
    control: AbstractControl,
    errorFlag: 'userNotFoundError' | 'userExistsError' | 'passwordMatchError',
    validator: ValidatorFn,
    onReset: () => void
  ): void {
    this[errorFlag] = true;
    control.addValidators(validator);
    control.updateValueAndValidity({ emitEvent: true });

    control.valueChanges.pipe(first()).subscribe(() => {
      this[errorFlag] = false;
      control.removeValidators(validator);
      control.updateValueAndValidity();
      onReset();
    });
  }

  setUser(type: string, userEmail?: string): void {
    const user = {
      username: userEmail || '',
      password: '',
      type: type
    };
    localStorage.setItem('user', JSON.stringify(user));
  }

  handleCaptcha(token: string) {
    this.captchaToken = token;
  }

  toggleAuthType(): void {
    this.isSignUp = !this.isSignUp;
    this.authForm.reset();
  }

  closeModal(): void {
    this.activeModal.close();
    if (!this.closeAndNavigate) return;
    this.router.navigate(['/']);
    localStorage.removeItem(ELocalStorageKey.RedirectUrl);
    this.navigationIntentService.clearIntent();
  }

  checkScreenSize(): void {
    this.isMobile = window.innerWidth < 640;
  }

  openForgotPassword(): void {
    this.closeModal();
    if (window.innerWidth < 640) {
      this.modal.open(PasswordResetComponent, {
        size: 'sm',
        windowClass: 'modal-fullscreen'
      });
    } else {
      this.modal.open(PasswordResetComponent);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkScreenSize();
  }

  onEnterKeyPress(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.hasAttribute('tabindex')) {
      return;
    }

    event.preventDefault();

    if (this.authForm.valid) {
      this.executeRecaptcha();
    }
  }

  isAnyAuthInProgress(): boolean {
    return this.signUpButton.isLoading || this.loginButton.isLoading || this.googleButton.isLoading;
  }

  ngOnDestroy(): void {
    document.body.style.removeProperty('overflow');
  }
}
